import ClearIcon from '@mui/icons-material/ClearOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import AxiosConfig from '../../../../config/AxiosConfig';
import TaskStatusChip from '../../utils/common/TaskStatusChip';
import UserAssignmentAutocomplete from '../../utils/common/UserAssignmentAutocomplete';
import Note from './Note';
import { getLastNote } from './utils';

// Helpers functions
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray?.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array?.map((el, index) => [el, index]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}

// Constants
const DEFAULT_ORDER = 'desc';
const DEFAULT_ORDER_BY = '_id';

const headers = [
  {
    id: 'ticket',
    align: 'left',
    label: 'Référence Ticket',
  },
  {
    id: 'status',
    align: 'left',
    label: 'Status',
  },
  {
    id: 'type',
    align: 'left',
    label: 'Type de tâche',
  },
  {
    id: 'note',
    align: 'left',
    label: 'Note',
  },
  {
    id: 'assignedTo',
    align: 'center',
    label: 'Assignation',
  },
];

const DashboardSCTaskTableToolbar = (props) => {
  const {
    loading,
    validating,
    service,
    titleIcon,
    dashboardData,
    setIsDone,
    setTicketRef,
    setAssignedUserTask,
    setCreatorUserTask,
    nb,
  } = props;

  const [selectedAssignedUser, setSelectedAssignedUser] = useState(
    dashboardData?.assignedUserTask
  );
  const [selectedCreatorUser, setSelectedCreatorUser] = useState(
    dashboardData?.creatorUserTask
  );

  useEffect(() => {
    if (selectedAssignedUser) {
      setAssignedUserTask(selectedAssignedUser);
    } else if (selectedAssignedUser === '') {
      setAssignedUserTask('');
    } else {
      setAssignedUserTask(null);
    }
  }, [selectedAssignedUser]);

  useEffect(() => {
    if (selectedCreatorUser) {
      setCreatorUserTask(selectedCreatorUser);
    } else if (selectedCreatorUser === '') {
      setCreatorUserTask('');
    } else {
      setCreatorUserTask(null);
    }
  }, [selectedCreatorUser]);

  const handleAssignedUserCheckbox = () => {
    if (dashboardData?.assignedUserTask === null) {
      setAssignedUserTask('');
    } else {
      setAssignedUserTask(null);
      setSelectedAssignedUser(null);
    }
  };

  const handleCreatorUserCheckbox = () => {
    if (dashboardData?.creatorUserTask === null) {
      setCreatorUserTask('');
    } else {
      setCreatorUserTask(null);
      setSelectedCreatorUser(null);
    }
  };

  const handleTicketRefCheckbox = () => {
    setTicketRef(dashboardData?.ticketRef === null ? '' : null);
  };

  const handleTicketRefChange = (e) => {
    setTicketRef(e.target.value);
  };

  const handleTicketRefClear = () => {
    setTicketRef('');
  };

  const handleIsDoneCheckbox = () => {
    setIsDone(!dashboardData?.isDone);
  };

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      {titleIcon}
      <Typography
        component={'span'}
        sx={{ flex: '1 1 100%' }}
        variant="h6"
        id="tableTitle"
      >
        Tâches :{' '}
        {loading || validating ? (
          <CircularProgress color="info" size={'0.8em'} />
        ) : (
          nb
        )}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          whiteSpace: 'nowrap',
          justifyContent: 'space-around',
          width: '100%',
        }}
      >
        {dashboardData?.assignedUserTask !== undefined && (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={dashboardData?.assignedUserTask !== null}
                  disabled={dashboardData?.toModerate || service === 'MP'}
                  onChange={handleAssignedUserCheckbox}
                />
              }
              label="Assigné à"
            />
            {dashboardData?.assignedUserTask !== null && (
              <UserAssignmentAutocomplete
                type={'task'}
                setSelectedUser={setSelectedAssignedUser}
                selectedUser={selectedAssignedUser}
                isSubmit={true}
              />
            )}
          </FormGroup>
        )}
        {dashboardData?.creatorUserTask !== undefined && (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={dashboardData?.creatorUserTask !== null}
                  disabled={dashboardData?.toModerate || service === 'MP'}
                  onChange={handleCreatorUserCheckbox}
                />
              }
              label="Créé par"
            />
            {dashboardData?.creatorUserTask !== null && (
              <UserAssignmentAutocomplete
                type={'task'}
                setSelectedUser={setSelectedCreatorUser}
                selectedUser={selectedCreatorUser}
                isSubmit={true}
              />
            )}
          </FormGroup>
        )}

        {/* Terminé */}
        {dashboardData?.isDone !== undefined && (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!dashboardData?.isDone}
                  disabled={service === 'MP'}
                  onChange={handleIsDoneCheckbox}
                />
              }
              label="Terminé"
            />
          </FormGroup>
        )}

        {/* Numéro de ticket */}
        {dashboardData?.ticketRef !== undefined && (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={
                    dashboardData?.ticketRef !== undefined &&
                    dashboardData?.ticketRef !== null
                  }
                  disabled={service === 'MP'}
                  onChange={handleTicketRefCheckbox}
                />
              }
              label="Numéro de ticket"
            />
            {dashboardData?.ticketRef !== undefined &&
            dashboardData?.ticketRef !== null ? (
              <TextField
                placeholder="N° Ticket"
                onChange={handleTicketRefChange}
                value={dashboardData?.ticketRef}
                variant="outlined"
                size="small"
                slotProps={{
                  input: {
                    type: 'text',
                    style: { fontSize: 12 },
                    endAdornment: (
                      <IconButton onClick={handleTicketRefClear} edge="end">
                        <ClearIcon />
                      </IconButton>
                    ),
                  },
                }}
              />
            ) : null}
          </FormGroup>
        )}
      </Box>
    </Toolbar>
  );
};

const DashboardSCTaskTableHead = (props) => {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (newOrderBy) => (event) => {
    onRequestSort(event, newOrderBy);
  };

  return (
    <TableHead sx={{ whiteSpace: 'nowrap' }}>
      <TableRow>
        {headers?.map((header) => (
          <TableCell
            key={header?.id}
            align={header?.align}
            sortDirection={orderBy === header?.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === header?.id}
              direction={orderBy === header?.id ? order : 'asc'}
              onClick={createSortHandler(header?.id)}
            >
              {header?.label}
              {orderBy === header?.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const DashboardSCTaskRow = ({ row, mutate }) => {
  const navigate = useNavigate();
  const profile = useSelector((state) => state?.login?.profile);
  const [selectedUser, setSelectedUser] = useState(row?.assignedTo || null);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { mutate: mutateUsers } = useSWRConfig();
  const { mutate: mutateTask } = useSWRConfig();

  useEffect(() => {
    if (
      selectedUser &&
      selectedUser?._id !== row?.assignedTo?._id &&
      profile?._id
    ) {
      setIsLoading(true);
      AxiosConfig.create('post', `/tasks/${row?._id}/assignUser`)
        .call({
          assignedBy: profile._id,
          assignedTo: selectedUser._id,
        })
        .exit(() => {
          mutateUsers(`/users/`);
          mutateTask(`/tasks/`);
          mutate();
        })
        .catch((err) => console.error(err))
        .finally(() => setIsLoading(false));
    }
  }, [selectedUser?._id, row?.assignedTo?._id]);

  const handleNavigateToProject = useCallback(() => {
    navigate(
      `/project/${row?.ticket?.project?._id}/tickets/${row?.ticket?._id}/tasks/${row?._id}`
    );
  }, [navigate, row]);

  return (
    <TableRow hover tabIndex={-1} sx={{ cursor: 'pointer' }} value={row?._id}>
      <TableCell>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleNavigateToProject}
          sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
        >
          {row?.ticket?.reference}
        </Button>
      </TableCell>

      <TableCell>
        <TaskStatusChip status={row?.status} />
      </TableCell>

      <TableCell>{row?.action?.name}</TableCell>

      <TableCell>
        <Box>
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fill: '#000' }} />}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography sx={{ flex: 1, fontSize: '1em' }}>
                  <Note
                    isLastNote
                    note={getLastNote(row.notes)}
                    expanded={expanded}
                  />
                </Typography>
                <Typography
                  sx={{
                    minWidth: '20%',
                    fontSize: '1em',
                    textAlign: 'end',
                    fontStyle: 'italic',
                    pr: 2,
                  }}
                >
                  {row?.notes?.length
                    ? `${row.notes.length} Note(s)`
                    : 'Aucune note'}
                </Typography>
              </Box>
            </AccordionSummary>

            {row?.notes
              ?.filter((note) => note?._id !== getLastNote(row.notes)?._id)
              .map((taskNote) => (
                <React.Fragment key={taskNote?._id}>
                  <Divider />
                  <AccordionDetails>
                    <Note note={taskNote} expanded={expanded} />
                  </AccordionDetails>
                </React.Fragment>
              ))}
          </Accordion>
        </Box>
      </TableCell>

      <TableCell align="right">
        {isLoading ? (
          <Skeleton variant="rectangular" width={150} height={30} />
        ) : (
          <UserAssignmentAutocomplete
            assignmentId={row?.assignedTo?._id}
            setSelectedUser={setSelectedUser}
            selectedUser={selectedUser}
            type={'task'}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

const DashboardSCTaskTable = (props) => {
  const {
    loading,
    validating,
    datas,
    service,
    dashboardData,
    setIsDone,
    setTicketRef,
    setAssignedUserTask,
    setCreatorUserTask,
    limit,
    setLimit,
    page,
    setPage,
    total,
    mutate,
    titleIcon,
  } = props;
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [orderedRows, setOrderedRows] = useState(null);

  useEffect(() => {
    const sortedRows = stableSort(
      datas,
      getComparator(DEFAULT_ORDER, DEFAULT_ORDER_BY)
    );

    setOrderedRows(sortedRows);
  }, [datas]);

  const handleRequestSort = useCallback(
    (event, newOrderBy) => {
      const isAsc = orderBy === newOrderBy && order === 'asc';
      const toggledOrder = isAsc ? 'desc' : 'asc';
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);

      const sortedRows = stableSort(
        datas,
        getComparator(toggledOrder, newOrderBy)
      );

      setOrderedRows(sortedRows);
    },
    [order, orderBy, page, datas]
  );

  const handleChangePage = useCallback(
    (event, newPage) => {
      setPage(newPage);

      const sortedRows = stableSort(datas, getComparator(order, orderBy));

      setOrderedRows(sortedRows);
    },
    [order, orderBy, limit, datas]
  );

  const handleChangeLimit = useCallback(
    (event) => {
      const updatedLimit = parseInt(event.target.value, 10);
      setLimit(updatedLimit);
      setPage(0);

      const sortedRows = stableSort(datas, getComparator(order, orderBy));

      setOrderedRows(sortedRows);
    },
    [order, orderBy, datas]
  );

  return (
    <Paper elevation={24} sx={{ width: '100%', mb: 2 }}>
      <DashboardSCTaskTableToolbar
        loading={loading}
        validating={validating}
        service={service}
        titleIcon={titleIcon}
        dashboardData={dashboardData}
        setIsDone={setIsDone}
        setTicketRef={setTicketRef}
        setAssignedUserTask={setAssignedUserTask}
        setCreatorUserTask={setCreatorUserTask}
        nb={total}
      />
      <TableContainer>
        <Table sx={{ minWidth: 750 }} size="small">
          <DashboardSCTaskTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {loading ? (
              <TableRow>
                {headers?.map((h) => (
                  <TableCell key={h?.id}>
                    <Skeleton variant="text" sx={{ fontSize: '2em' }} />
                  </TableCell>
                ))}
              </TableRow>
            ) : orderedRows ? (
              orderedRows?.map((row) => {
                return row?.ticket?.reference ? (
                  <DashboardSCTaskRow
                    key={row?._id}
                    row={row}
                    mutate={mutate}
                  />
                ) : (
                  <TableRow>
                    {headers?.map((h) => (
                      <TableCell key={h?.id}>
                        <Skeleton variant="text" sx={{ fontSize: '2em' }} />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      {datas ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {validating && <CircularProgress color="info" size={'1.3em'} />}
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={total}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeLimit}
          />
        </Box>
      ) : null}
    </Paper>
  );
};

export default DashboardSCTaskTable;
